import React, { useEffect, useState } from 'react';
import { CheckCircle2, Minus, Package, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { OneTimeOrderReview } from './OneTimeOrderReview';
import { BundleBadge, BundleContents } from './BundleLineDetails';
import { ProductSourceModal } from '../ProductSourceModal';
import type { Product as PickerProduct } from '../ProductSourceModal';
import { useOneTimeOrderPricing } from '../../hooks/useOneTimeOrderPricing';
import { oneTimeOrderInventory } from '../../data/mockOneTimeOrder';
export interface OneTimeOrderBundleItem {
  name: string;
  quantity: number;
}
export interface OneTimeOrderProduct {
  id: string;
  name: string;
  price: number;
  // Present when the product is a bundle; price is per bundle
  bundleContents?: OneTimeOrderBundleItem[];
}
export interface OneTimeOrderLine extends OneTimeOrderProduct {
  quantity: number;
}
interface OneTimeOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerName: string;
  initialLines: OneTimeOrderLine[];
  availableProducts: OneTimeOrderProduct[];
  shippingAddressLines: string[];
  deliveryMethod: string;
  paymentMethodLabel: string;
}
type Step = 'products' | 'review' | 'done';
export function OneTimeOrderModal({
  isOpen,
  onClose,
  customerName,
  initialLines,
  availableProducts,
  shippingAddressLines,
  deliveryMethod,
  paymentMethodLabel
}: OneTimeOrderModalProps) {
  const [step, setStep] = useState<Step>('products');
  const [lines, setLines] = useState<OneTimeOrderLine[]>(initialLines);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const pricing = useOneTimeOrderPricing(lines);

  // Start fresh from the subscription's current products each time it opens
  useEffect(() => {
    if (isOpen) {
      setStep('products');
      setLines(initialLines);
      setIsPickerOpen(false);
      setIsPaying(false);
      pricing.reset();
    }
  }, [isOpen]);
  useEffect(() => {
    // While the product picker is open, Escape belongs to it
    if (!isOpen || isPickerOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, isPickerOpen, onClose]);
  if (!isOpen) return null;
  const updateQuantity = (id: string, delta: number) => {
    setLines(prev => prev.map(line => line.id === id ? {
      ...line,
      quantity: Math.max(1, line.quantity + delta)
    } : line));
  };
  const removeLine = (id: string) => setLines(prev => prev.filter(line => line.id !== id));
  // Confirming the picker keeps checked lines (with their quantities),
  // adds newly checked products at quantity 1 and drops unchecked ones
  const applyPickerSelection = (selectedIds: string[]) => {
    setLines(prev => {
      const kept = prev.filter(line => selectedIds.includes(line.id));
      const added = selectedIds.filter(id => !prev.some(line => line.id === id)).map(id => availableProducts.find(product => product.id === id)).filter((product): product is OneTimeOrderProduct => Boolean(product)).map(product => ({
        ...product,
        quantity: 1
      }));
      return [...kept, ...added];
    });
  };
  const pickerProducts: PickerProduct[] = availableProducts.map(product => {
    const inventory = oneTimeOrderInventory[product.id];
    const stock = inventory ? inventory.stock : null;
    return {
      id: product.id,
      name: product.name,
      subtitle: inventory?.subtitle ?? '750ml',
      price: product.price,
      stock,
      status: getStockStatus(stock),
      color: inventory?.color ?? '#F1F5F9',
      sku: inventory?.sku,
      isBundle: Boolean(product.bundleContents)
    };
  });
  const totalBottles = lines.reduce((sum, line) => sum + line.quantity, 0);
  const subtotal = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
  const formatMoney = (value: number) => `$${value.toFixed(2)}`;
  const handlePay = async () => {
    setIsPaying(true);
    await new Promise(resolve => setTimeout(resolve, 900));
    setIsPaying(false);
    setStep('done');
  };
  const stepIndex = step === 'products' ? 1 : 2;
  return <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} data-id="element-4911" data-mp="1nbjb5wp1nsqq" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-4912" data-mp="1ie9lcfxqtf2d">
        <div role="dialog" aria-modal="true" aria-labelledby="one-time-order-title" className={`bg-white rounded-lg shadow-xl w-full ${step === 'review' ? 'max-w-[780px]' : 'max-w-[560px]'} max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto transition-[max-width]`} data-id="element-4913" data-mp="1dgzqgkrl4b75">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-4914" data-mp="zhrmlekh8ewm">
            <div className="flex items-center gap-3" data-id="element-4915" data-mp="2ys9jbld12umz">
              <div className="w-10 h-10 rounded-md bg-[#E0F2FE] flex items-center justify-center" data-id="element-4916" data-mp="2ttoipktv68y0">
                <ShoppingBag className="w-5 h-5 text-[#0EA5E9]" data-id="element-4917" data-mp="31e8vhcrwlddn" />
              </div>
              <div data-id="element-4918" data-mp="36c0ns7wgn1id">
                <h2 id="one-time-order-title" className="text-[18px] font-semibold text-[#0F172A]" data-id="element-4919" data-mp="36bijcnzn9gzd">
                  Create one-time order
                </h2>
                <p className="text-[13px] text-[#64748B]" data-id="element-4920" data-mp="3g2dginefi0zl">
                  {step === 'done' ? `For ${customerName}` : `Step ${stepIndex} of 2 · ${step === 'products' ? 'Choose products' : 'Review and pay'}`}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-4921" data-mp="2ndy5ebyf42tj">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-4922" data-mp="2wee2n3okidpy" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 overflow-y-auto space-y-5" data-id="element-4923" data-mp="7n37evc4fvib">
            {step === 'products' && <>
                <div data-id="element-4924" data-mp="bndpv7h3oee2">
                  <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-4925" data-mp="bmu3nekf33u8">
                    Choose products
                  </h3>
                  <p className="text-[13px] text-[#64748B] mt-1" data-id="element-4926" data-mp="28qm79u4qvnqw">
                    Charged to the subscription's payment method and shipped to
                    its address with its delivery method. The subscription
                    itself is not changed.
                  </p>
                </div>

                {lines.length === 0 ? <div className="border border-dashed border-[#CBD5E1] rounded-md px-4 py-6 text-center text-[14px] text-[#64748B]" data-id="element-4927" data-mp="1p0afq9g5sca0">
                    No products yet. Add at least one to continue.
                  </div> : <ul className="border border-[#E2E8F0] rounded-md divide-y divide-[#E2E8F0]" data-id="element-4928" data-mp="1yqvbzq8d14pr">
                    {lines.map(line => <li key={line.id} className="flex items-center gap-3 px-4 py-3" data-id="element-4929" data-mp="1tz3jha5n8owz">
                        <Package className="w-4 h-4 text-[#94A3B8] flex-shrink-0" data-id="element-4930" data-mp="169j2uxwyq3qv" />
                        <div className="flex-1 min-w-0" data-id="element-4931" data-mp="11bra61ohudlm">
                          <p className="text-[14px] font-medium text-[#0F172A] flex items-center gap-2" data-id="element-4932" data-mp="11953035im03x">
                            {line.name}
                            {line.bundleContents && <BundleBadge data-id="element-4933" data-mp="3hq2zi1qwifmw" />}
                          </p>
                          <p className="text-[13px] text-[#94A3B8]" data-id="element-4934" data-mp="33yemzxcqpp5e">
                            {formatMoney(line.price)} {line.bundleContents ? 'per bundle' : 'each'}
                          </p>
                          {line.bundleContents && <BundleContents contents={line.bundleContents} data-id="element-4935" data-mp="iherty52r9p" />}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4936" data-mp="4hgcathjjlr4">
                          <button type="button" onClick={() => updateQuantity(line.id, -1)} disabled={line.quantity <= 1} aria-label={`Decrease ${line.name}`} className="w-8 h-8 flex items-center justify-center rounded-md border border-[#CBD5E1] bg-white text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-4937" data-mp="3mow4f5s4gybg">
                            <Minus className="w-3.5 h-3.5" data-id="element-4938" data-mp="2g67nwqfmr42a" />
                          </button>
                          <span className="w-6 text-center text-[14px] font-semibold text-[#0F172A]" aria-live="polite" data-id="element-4939" data-mp="37tsilwzdpqra">
                            {line.quantity}
                          </span>
                          <button type="button" onClick={() => updateQuantity(line.id, 1)} aria-label={`Increase ${line.name}`} className="w-8 h-8 flex items-center justify-center rounded-md border border-[#0EA5E9] bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-colors" data-id="element-4940" data-mp="2xzs63a1rpj8j">
                            <Plus className="w-3.5 h-3.5" data-id="element-4941" data-mp="3lns7r50mau1w" />
                          </button>
                        </div>
                        <button type="button" onClick={() => removeLine(line.id)} aria-label={`Remove ${line.name}`} className="p-2 rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors" data-id="element-4942" data-mp="3gugw8qkv1p3n">
                          <Trash2 className="w-4 h-4" data-id="element-4943" data-mp="8gqilwcs618q" />
                        </button>
                      </li>)}
                  </ul>}

                <div data-id="element-4944" data-mp="gqfqzp20dad3">
                  <button type="button" onClick={() => setIsPickerOpen(true)} aria-haspopup="dialog" className="inline-flex items-center gap-2 h-9 px-4 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-4945" data-mp="gqyz6e1uwkdx">
                    <Plus className="w-4 h-4" data-id="element-4946" data-mp="uq6z595ufwj6" />
                    Add products
                  </button>
                </div>
              </>}

            {step === 'review' && <>
                <OneTimeOrderReview lines={lines} pricing={pricing} shippingAddressLines={shippingAddressLines} deliveryMethod={deliveryMethod} paymentMethodLabel={paymentMethodLabel} data-id="element-4947" data-mp="13pbuk6m3z9r4" />
              </>}

            {step === 'done' && <div className="py-6 text-center" data-id="element-4948" data-mp="1debxu27n8rb4">
                <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto" data-id="element-4949" data-mp="1ieq87qwpwdcb" />
                <h3 className="text-[16px] font-semibold text-[#0F172A] mt-3" data-id="element-4950" data-mp="1igkqsykintd8">
                  Order created
                </h3>
                <p className="text-[14px] text-[#64748B] mt-1" data-id="element-4951" data-mp="2jta823lgqnsb">
                  {formatMoney(pricing.total)} (plus tax) charged to{' '}
                  {paymentMethodLabel}. The
                  subscription is unchanged.
                </p>
              </div>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between gap-3 bg-white" data-id="element-4952" data-mp="2xk5cn3oxiiq3">
            {step === 'products' && <>
                <span className="text-[13px] text-[#64748B]" data-id="element-4953" data-mp="32j8hoo4wsoy2">
                  {totalBottles} {totalBottles === 1 ? 'bottle' : 'bottles'} ·{' '}
                  {formatMoney(subtotal)}
                </span>
                <div className="flex items-center gap-3" data-id="element-4954" data-mp="3bi3pg4nlq73h">
                  <button type="button" onClick={onClose} className="h-10 px-5 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-4955" data-mp="3gi80vg9nkbmu">
                    Cancel
                  </button>
                  <button type="button" onClick={() => setStep('review')} disabled={lines.length === 0} className="h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-4956" data-mp="1elqw9u99dzbm">
                    Continue
                  </button>
                </div>
              </>}
            {step === 'review' && <>
                <button type="button" onClick={() => setStep('products')} disabled={isPaying} className="h-10 px-5 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] disabled:opacity-50 transition-colors" data-id="element-4957" data-mp="1xkcwz80v5of0">
                  Back
                </button>
                <button type="button" onClick={handlePay} disabled={isPaying} className="h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] disabled:opacity-70 transition-colors" data-id="element-4958" data-mp="1jl3qx16dgaor">
                  {isPaying ? 'Charging…' : 'Confirm and charge'}
                </button>
              </>}
            {step === 'done' && <button type="button" onClick={onClose} className="ml-auto h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-4959" data-mp="9ndhk2hogrdo">
                Done
              </button>}
          </div>
        </div>
      </div>

      <ProductSourceModal isOpen={isPickerOpen} onClose={() => setIsPickerOpen(false)} onConfirm={applyPickerSelection} mode="custom" products={pickerProducts} initialSelectedIds={lines.map(line => line.id)} data-id="element-4960" data-mp="4n7vr2y39z3i" />
    </>;
}
function getStockStatus(stock: number | null): PickerProduct['status'] {
  if (stock === null) return 'Inventory Not Tracked';
  if (stock === 0) return 'Out of Stock';
  if (stock <= 10) return 'Low Stock';
  return 'In Stock';
}